// Outbound calling — the "posted → called → revenue" report, by Pacific
// posting day (outbound_calling PRP v2). READ-ONLY reporting page: two
// admin-api GETs off the lead-sales ledger joined to td-calls. No config, no
// on/off switch — that lives on the Outbound (Witzzy) page. Gated:
// sec-lead-sales, same section as the rest of Lead Sales.
//
// Reference: stages/02_prp/output/outbound_calling_prp.md (v2) — "What" →
// Endpoints for the response shapes, AC13 for this page, "Known Gotchas".
//
// Two independent fetches, deliberately separate:
//   - Cohorts (api.outboundCohorts): one row per requested Pacific posting
//     day (this page re-sorts newest first, never trusting server order),
//     plus a server-computed `totals` row. The totals row is ALWAYS rendered
//     straight from the response's own `totals` field — NEVER recomputed
//     client-side (percentiles don't sum, and a per-day cap can make a naive
//     client-side sum silently wrong; see the PRP's Known Gotchas).
//   - Leads (api.outboundLeads): the per-day drill-down, opened by clicking
//     a cohort row. Offset-paginated — "Load more" advances the previous
//     page's `next_offset`; the button hides once that's null. Never a
//     cursor: the PRP deliberately keeps pagination free of anything
//     reversible to a phone number.
//
// Modeled on pages/GrtPostback.tsx (table/loading/empty-state patterns) and
// pages/LeadSalesWitzzy.tsx (tokens, range-picker shape, StatCard/SkeletonStat
// precedent — this page's table needs no StatCard strip, but keeps the same
// select/Card/Skeleton/EmptyState vocabulary).

import { useCallback, useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import {
  api,
  type OutboundCohortRow,
  type OutboundCohortsResponse,
  type OutboundLeadRow,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  tokens,
} from "../lib/theme";

// Range picker options (AC13: 1–7 days, default 7 — the PRP's approved cap).
const DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7] as const;
const DEFAULT_DAYS = 7;

// The drill-down's per-page size. The server clamps `limit` to 1..200; 50
// keeps a click responsive while "Load more" still means something.
const LEADS_PAGE_SIZE = 50;

// D0..D6 — the offsets array's fixed length (the server's WINDOW_DAYS
// attribution window; always 7, independent of how many cohort days were
// requested via the range picker above).
const OFFSET_COLUMNS = 7;

// Column bookkeeping for colSpan (skeleton rows + the empty-state row) so the
// spans can't silently drift from the real <Th> count above them.
// Day, Posted, Called, % Called · D0..D6 · Connected, Paid, Revenue,
// Elsewhere, Median min.
const TOTAL_COLUMNS = 4 + OFFSET_COLUMNS + 5;
const NON_DAY_COLUMNS = TOTAL_COLUMNS - 1;

function fmtCount(n: number): string {
  return (n ?? 0).toLocaleString();
}

/** cents → "$1,234.56". revenue_cents is never null on these rows — no
 *  nullable guard needed, unlike the minutes fields below. */
function fmtRevenue(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** called_target/posted as a whole percent. Guards posted === 0 → "—" (never
 *  NaN/Infinity) — applies wherever a % Called cell is rendered, per-day row
 *  or totals. */
function pctCalled(calledTarget: number, posted: number): string {
  if (posted === 0) return "—";
  return `${Math.round((calledTarget / posted) * 100)}%`;
}

/** Minutes (possibly fractional) → a whole-minute label; null/undefined →
 *  "—" (never NaN, never 0). Shared by the cohort median column and the
 *  per-lead minutes_to_call column. */
function fmtMinutes(min: number | null | undefined): string {
  if (min === null || min === undefined) return "—";
  return `${Math.round(min)} min`;
}

/** epoch ms → a compact local date + time (viewer's timezone); undefined
 *  (a lead with no target call has no first_target_at_ms) → "—". */
function fmtDateTime(ms: number | undefined): string {
  if (ms === undefined) return "—";
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString(
    [],
    { hour: "2-digit", minute: "2-digit" },
  )}`;
}

export default function OutboundCalling() {
  const [days, setDays] = useState<number>(DEFAULT_DAYS);
  const [resp, setResp] = useState<OutboundCohortsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .outboundCohorts({ days })
      .then((r) => {
        setResp(r);
        setError(null);
        setRefreshedAt(new Date());
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Drill-down: offset-paginated leads for one clicked Pacific day ------
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [leads, setLeads] = useState<OutboundLeadRow[]>([]);
  const [leadsTotal, setLeadsTotal] = useState<number | null>(null);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState<string | null>(null);

  const fetchLeadsPage = useCallback(
    (day: string, offset: number, replace: boolean) => {
      setLeadsLoading(true);
      api
        .outboundLeads({ day, limit: LEADS_PAGE_SIZE, offset })
        .then((r) => {
          setLeads((prev) => (replace ? r.leads : [...prev, ...r.leads]));
          setLeadsTotal(r.total);
          setNextOffset(r.next_offset);
          setLeadsError(null);
        })
        .catch((err: unknown) => {
          setLeadsError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLeadsLoading(false));
    },
    [],
  );

  // Clicking any cohort row (including an empty-state "no leads posted" row)
  // opens the drill-down for that day — a zero-posted day simply resolves to
  // an empty leads list.
  const openDay = useCallback(
    (day: string) => {
      setSelectedDay(day);
      setLeads([]);
      setLeadsTotal(null);
      setNextOffset(null);
      setLeadsError(null);
      fetchLeadsPage(day, 0, true);
    },
    [fetchLeadsPage],
  );

  const loadMoreLeads = useCallback(() => {
    if (selectedDay === null || nextOffset === null) return;
    fetchLeadsPage(selectedDay, nextOffset, false);
  }, [selectedDay, nextOffset, fetchLeadsPage]);

  const selectStyle: CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  // Newest posting day first — this page's own contract (AC13), asserted
  // here rather than assumed of the API response order.
  const sortedCohorts = [...(resp?.cohorts ?? [])].sort((a, b) =>
    b.day.localeCompare(a.day),
  );

  return (
    <div>
      <PageHeader
        title="Outbound calling"
        subtitle="Posted → called → revenue, by Pacific posting day (7-day attribution window)"
        actions={
          <div
            style={{
              display: "flex",
              gap: tokens.space.sm,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              style={selectStyle}
              aria-label="Days"
            >
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d === 1 ? "Last 1 day" : `Last ${d} days`}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              style={buttonStyle("ghost", loading)}
              aria-label="Refresh"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            {refreshedAt && (
              <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
                Updated{" "}
                {refreshedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
          </div>
        }
      />

      {error && !resp ? (
        <Card>
          <EmptyState title="Failed to load Outbound calling report" hint={error} />
        </Card>
      ) : (
        <>
          {resp?.partial && (
            <div
              role="alert"
              style={{
                marginBottom: tokens.space.md,
                padding: `${tokens.space.md}px ${tokens.space.lg}px`,
                borderRadius: tokens.radius.md,
                background: "rgba(212,168,83,0.10)",
                border: "1px solid rgba(212,168,83,0.35)",
                color: tokens.color.warning,
                fontSize: tokens.font.size.sm,
              }}
            >
              some data could not be read; counts may be low
            </div>
          )}

          <Card padding={0} style={{ marginBottom: tokens.space.xl }}>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: tokens.font.size.sm,
                }}
              >
                <thead>
                  <tr>
                    <Th>Day</Th>
                    <Th align="right">Posted</Th>
                    <Th align="right">Called</Th>
                    <Th align="right">% Called</Th>
                    {Array.from({ length: OFFSET_COLUMNS }).map((_, i) => (
                      <Th key={i} align="center">{`D${i}`}</Th>
                    ))}
                    <Th align="right">Connected</Th>
                    <Th align="right">Paid</Th>
                    <Th align="right">Revenue</Th>
                    <Th align="right">Elsewhere</Th>
                    <Th align="right">Median min</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading && !resp ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={`sk-${i}`}>
                        <Td colSpan={TOTAL_COLUMNS}>
                          <Skeleton height={20} />
                        </Td>
                      </tr>
                    ))
                  ) : (
                    <>
                      {sortedCohorts.map((row, idx) => (
                        <CohortRow key={row.day} row={row} idx={idx} onOpen={openDay} />
                      ))}
                      {resp && (
                        <tr
                          style={{
                            fontWeight: 700,
                            borderTop: `2px solid ${tokens.color.borderMid}`,
                          }}
                        >
                          <Td>Total</Td>
                          <Td align="right">{fmtCount(resp.totals.posted)}</Td>
                          <Td align="right">{fmtCount(resp.totals.called_target)}</Td>
                          <Td align="right">
                            {pctCalled(resp.totals.called_target, resp.totals.posted)}
                          </Td>
                          {Array.from({ length: OFFSET_COLUMNS }).map((_, i) => (
                            <Td key={i} align="center">
                              —
                            </Td>
                          ))}
                          <Td align="right">{fmtCount(resp.totals.connected)}</Td>
                          <Td align="right">{fmtCount(resp.totals.paid)}</Td>
                          <Td align="right" mono>
                            {fmtRevenue(resp.totals.revenue_cents)}
                          </Td>
                          <Td align="right">{fmtCount(resp.totals.called_elsewhere)}</Td>
                          <Td align="right">{fmtMinutes(resp.totals.median_minutes_to_call)}</Td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {selectedDay && (
        <Card
          title={`Leads posted ${selectedDay}`}
          action={
            <button
              type="button"
              onClick={() => setSelectedDay(null)}
              style={buttonStyle("ghost")}
              aria-label="Close drill-down"
            >
              Close
            </button>
          }
          style={{ marginBottom: tokens.space.xl }}
        >
          {leadsTotal !== null && (
            <div
              style={{
                marginBottom: tokens.space.md,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
              }}
            >
              {fmtCount(leadsTotal)} lead{leadsTotal === 1 ? "" : "s"} total
            </div>
          )}

          {leadsError && leads.length === 0 ? (
            <EmptyState title="Failed to load leads" hint={leadsError} />
          ) : leadsLoading && leads.length === 0 ? (
            <>
              <Skeleton height={20} />
              <div style={{ height: tokens.space.sm }} />
              <Skeleton height={20} />
            </>
          ) : leads.length === 0 ? (
            <EmptyState title="No leads" hint="Nothing posted this day." />
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    fontSize: tokens.font.size.sm,
                  }}
                >
                  <thead>
                    <tr>
                      <Th>Phone</Th>
                      <Th>Sent</Th>
                      <Th>First call</Th>
                      <Th align="right">Min to call</Th>
                      <Th align="center">D-offset</Th>
                      <Th align="right">Target calls</Th>
                      <Th align="right">Other calls</Th>
                      <Th align="center">Connected</Th>
                      <Th align="center">Paid</Th>
                      <Th align="right">Revenue</Th>
                      <Th>Buyer</Th>
                      <Th align="center">Event sent</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l, idx) => (
                      <tr
                        key={`${l.phone_masked}-${l.sent_at_ms}-${idx}`}
                        style={{
                          background:
                            idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                        }}
                      >
                        <Td mono>{l.phone_masked}</Td>
                        <Td>{fmtDateTime(l.sent_at_ms)}</Td>
                        <Td>{fmtDateTime(l.first_target_at_ms)}</Td>
                        <Td align="right">{fmtMinutes(l.minutes_to_call)}</Td>
                        <Td align="center">
                          {l.day_offset !== undefined ? `D${l.day_offset}` : "—"}
                        </Td>
                        <Td align="right">{fmtCount(l.target_calls)}</Td>
                        <Td align="right">{fmtCount(l.other_calls)}</Td>
                        <Td align="center">{l.connected ? "✓" : "—"}</Td>
                        <Td align="center">{l.paid ? "✓" : "—"}</Td>
                        <Td align="right" mono>
                          {fmtRevenue(l.revenue_cents)}
                        </Td>
                        <Td>{l.buyer_name ?? "—"}</Td>
                        <Td align="center">{l.event_sent ? "✓" : "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {nextOffset !== null && (
                <div style={{ padding: tokens.space.md, textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={loadMoreLeads}
                    disabled={leadsLoading}
                    style={buttonStyle("ghost", leadsLoading)}
                  >
                    {leadsLoading ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
}

/** One cohort row. A day with posted === 0 renders the empty-state row
 *  ("no leads posted") in place of the normal cells — never a normal row
 *  with a bunch of zeroed/dashed columns. Every row (including the
 *  empty-state one) opens the drill-down on click. */
function CohortRow({
  row,
  idx,
  onOpen,
}: {
  row: OutboundCohortRow;
  idx: number;
  onOpen: (day: string) => void;
}) {
  const zebra = idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)";

  if (row.posted === 0) {
    return (
      <tr onClick={() => onOpen(row.day)} style={{ cursor: "pointer", background: zebra }}>
        <Td>{row.day}</Td>
        <Td
          colSpan={NON_DAY_COLUMNS}
          style={{ color: tokens.color.fg2, fontStyle: "italic", whiteSpace: "normal" }}
        >
          no leads posted
        </Td>
      </tr>
    );
  }

  return (
    <tr onClick={() => onOpen(row.day)} style={{ cursor: "pointer", background: zebra }}>
      <Td style={{ whiteSpace: "normal" }}>
        <div>{row.day}</div>
        {!row.complete && (
          <div
            style={{
              marginTop: 2,
              color: tokens.color.warning,
              fontSize: tokens.font.size.xs,
            }}
          >
            {`partial — ${row.examined} of ${row.posted} leads checked`}
          </div>
        )}
      </Td>
      <Td align="right">{fmtCount(row.posted)}</Td>
      <Td align="right">{fmtCount(row.called_target)}</Td>
      <Td align="right">{pctCalled(row.called_target, row.posted)}</Td>
      {row.offsets.map((v, i) => (
        <Td key={i} align="center">
          {fmtCount(v)}
        </Td>
      ))}
      <Td align="right">{fmtCount(row.connected)}</Td>
      <Td align="right">{fmtCount(row.paid)}</Td>
      <Td align="right" mono>
        {fmtRevenue(row.revenue_cents)}
      </Td>
      <Td align="right">{fmtCount(row.called_elsewhere)}</Td>
      <Td align="right">{fmtMinutes(row.median_minutes_to_call)}</Td>
    </tr>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
  colSpan,
  style,
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
  mono?: boolean;
  colSpan?: number;
  style?: CSSProperties;
}) {
  return (
    <td
      colSpan={colSpan}
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </td>
  );
}
